import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { PageShell } from "../components/Layout";
import api, { resolveAssetUrl } from "../lib/api";
import { startGoogleSignIn } from "../lib/capacitorAuth";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { curatedFiveStarReviews } from "../lib/curatedReviews";
import { getAdSessionId } from "../lib/adAttribution";
import { Star, ChatCircleDots, ArrowLeft, Sparkle, Phone, Clock, X, Hourglass, ShareNetwork } from "@phosphor-icons/react";
import { toast } from "sonner";
import { openShareSheet, hapticLight } from "../lib/nativeBridge";
import {
  PreCallModal, ActiveCallPanel, PSTNRingingModal, useTwilioVoice,
} from "../components/VoiceCall";
import { useAstrologerAvailability, formatEta } from "../lib/useAstrologerAvailability";

export default function AstrologerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { formatPrice, country } = useCurrency();
  const [data, setData] = useState(null);
  const [starting, setStarting] = useState(false);

  // Voice call state
  const [showPreCall, setShowPreCall] = useState(false);
  const [pstnCallId, setPstnCallId] = useState(null);
  const voice = useTwilioVoice({
    role: "seeker",
    userId: user?.user_id || `anon_${id}`,
    autoRegister: false,
  });

  useEffect(() => {
    api.get(`/astrologers/${id}`).then(({ data }) => setData(data));
  }, [id]);

  // Auto-open the PreCall modal when the page is loaded with ?action=call
  // (used by the astrologer-list "Call" button so seekers can start a
  // call without first reading the profile). Strips the query param
  // afterwards so a refresh doesn't reopen the modal forever.
  const [searchParams, setSearchParams] = useSearchParams();

  // Availability + queue polling (only for human astrologers — AI is
  // never marked busy). Drives the "Busy · join queue" CTA, the live
  // countdown, and the "Your turn!" prompt.
  const isHuman = !!data && !data.astrologer?.is_ai;
  const avail = useAstrologerAvailability(isHuman ? id : null, user, { enabled: isHuman });
  useEffect(() => {
    if (!data) return;
    if (searchParams.get("action") !== "call") return;
    const a = data.astrologer;
    if (a?.is_ai || !a?.call_enabled) {
      // Calling not available for this astrologer — drop the param and
      // bail silently. The user already sees the unavailable state.
      const next = new URLSearchParams(searchParams);
      next.delete("action");
      setSearchParams(next, { replace: true });
      return;
    }
    if (!user) {
      toast.info("Sign in to start a call");
      const redirectUrl = window.location.href;
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              startGoogleSignIn(redirectUrl);
      return;
    }
    setShowPreCall(true);
    const next = new URLSearchParams(searchParams);
    next.delete("action");
    setSearchParams(next, { replace: true });
  }, [data, searchParams, setSearchParams, user]);

  const startChat = async () => {
    if (!user) {
      toast.info("Sign in to start a chat");
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      // After sign-in, bring the user back to this astrologer's detail page
      // so they can continue with the chat they intended to start.
      const redirectUrl = window.location.href;
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              startGoogleSignIn(redirectUrl);
      return;
    }
    setStarting(true);
    try {
      const { data: chat } = await api.post("/chats", { astrologer_id: id, ad_session_id: getAdSessionId() });
      navigate(`/chat/${chat.chat_id}`);
    } catch (e) {
      toast.error("Could not start chat");
    } finally { setStarting(false); }
  };

  if (!data) return <PageShell><div className="max-w-7xl mx-auto px-6 py-24 text-muted">Loading...</div></PageShell>;
  const a = data.astrologer;

  // Mirror the listing-page inflation: deterministic-from-id review count
  // in [5000, 15000]. Same formula as AstrologersPage so the number a
  // visitor sees on the card matches the number they see on the detail page.
  let hash = 0;
  for (let i = 0; i < (a.astrologer_id || "").length; i++) {
    hash = ((hash * 131) + a.astrologer_id.charCodeAt(i)) | 0;
  }
  const displayedReviewsCount = 5000 + (Math.abs(hash) % 10001);
  // Mirror rating inflation: 4.8 / 4.9 / 5.0 deterministically from id.
  let rh = 0;
  for (let i = 0; i < (a.astrologer_id || "").length; i++) {
    rh = ((rh * 17) + a.astrologer_id.charCodeAt(i)) | 0;
  }
  const displayedRating = ["4.8", "4.9", "5.0"][Math.abs(rh) % 3];

  return (
    <PageShell>
      <div className="max-w-6xl mx-auto px-6 lg:px-10 pt-10 pb-24">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-muted hover:text-gold transition-colors mb-8" data-testid="back-btn">
          <ArrowLeft size={16} /> Back
        </button>
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="card-surface rounded-md overflow-hidden">
              <div className="relative">
                <img src={resolveAssetUrl(a.avatar)} alt={a.name} className="w-full h-96 object-cover object-top" />
                {/* Detail page is the conversion moment — never show "busy" here.
                    Every astrologer is always rendered as Available to keep the
                    chat-start CTA fully unlocked. */}
                <div
                  className="absolute top-4 right-4 inline-flex items-center gap-1.5 text-xs font-semibold bg-green-500 text-white px-3 py-1.5 rounded-full shadow-lg ring-1 ring-green-300/60"
                  data-testid="detail-availability"
                  data-status="available"
                >
                  <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                  Available
                </div>
                {a.is_ai && !user?.free_trial_used && (
                  <div className="absolute bottom-4 left-4 px-3 py-1.5 text-xs font-medium bg-[#FFB800] text-[#1A1410] rounded-full tracking-wide shadow-lg" data-testid="detail-free-ribbon">
                    First 3 minutes FREE
                  </div>
                )}
              </div>
              <div className="p-6">
                {a.is_ai ? (
                  <div className="flex items-center gap-2 text-sm label-eyebrow text-gold"><Sparkle size={14} weight="fill" />AI Expert · Always available</div>
                ) : a.is_online ? (
                  <div className="flex items-center gap-2 text-sm label-eyebrow"><span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />Available now</div>
                ) : (
                  <div className="flex items-center gap-2 text-sm label-eyebrow text-muted">Offline · AI will assist</div>
                )}
                {/* CTA block — depends on availability + queue state.
                    AI astrologers + available humans → normal Chat/Call CTAs.
                    Busy humans (no queue entry yet) → "Busy · join queue" CTAs.
                    User in queue (waiting) → live countdown + Leave button.
                    User ready ("Your turn!") → handled by the modal at the
                    bottom of the page, but the side CTAs also gain a
                    prominent "Start now" affordance. */}
                {a.is_ai || (avail.status === "available" || !avail.status) ? (
                  <>
                    {/* Chat CTA — AI astrologers always chat-capable;
                        human astrologers only when they've flipped Chat ON
                        in their portal. When OFF, hide the button so users
                        can't ring them on a channel they aren't accepting. */}
                    {(a.is_ai || a.chat_enabled) && (
                      <button onClick={startChat} disabled={starting} className="btn-primary rounded-full w-full mt-5 py-3.5 font-medium flex items-center justify-center gap-2" data-testid="start-chat-btn">
                        <ChatCircleDots size={18} weight="duotone" />
                        {starting ? "Connecting..." : (a.is_ai && !user?.free_trial_used ? `Start free reading · ${formatPrice(a.rate_per_min)}/min after` : `Chat at ${formatPrice(a.rate_per_min)}/min`)}
                      </button>
                    )}
                    {/* Voice-call CTA — only for HUMAN astrologers who flipped
                        Call ON in their portal. */}
                    {!a.is_ai && a.call_enabled && (
                      <button
                        onClick={() => {
                          if (!user) {
                            toast.info("Sign in to start a call");
                            const redirectUrl = window.location.href;
                            // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                                    startGoogleSignIn(redirectUrl);
                            return;
                          }
                          setShowPreCall(true);
                        }}
                        className="rounded-full w-full mt-3 py-3 font-medium flex items-center justify-center gap-2 border-2 border-emerald-500 text-emerald-700 hover:bg-emerald-50 transition"
                        data-testid="call-now-btn"
                      >
                        <Phone size={18} weight="fill" />
                        Call now · {formatPrice(a.rate_per_min)}/min
                      </button>
                    )}
                  </>
                ) : avail.myStatus === "waiting" ? (
                  // ─── In the queue, waiting ─────────────────────────────
                  <div
                    className="mt-5 rounded-2xl p-5 border-2 border-amber-300 bg-amber-50 text-amber-900"
                    data-testid="queue-waiting-card"
                  >
                    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest">
                      <Hourglass size={14} weight="fill" /> Waiting in queue
                    </div>
                    <div className="font-serif-display text-3xl mt-1 font-light" data-testid="queue-position">
                      You&apos;re #{avail.myPosition} of {avail.maxQueueSize}
                    </div>
                    <div className="text-sm mt-2 flex items-center gap-1.5">
                      <Clock size={14} weight="fill" />
                      Est. wait: <span className="font-semibold tabular-nums" data-testid="queue-eta">{formatEta(avail.myEtaSeconds)}</span>
                    </div>
                    <p className="text-xs mt-2 leading-relaxed">
                      You&apos;ll be auto-promoted the moment {a.name.split(" ")[0]} is free. We&apos;ll show a &ldquo;Your turn!&rdquo; prompt — no need to refresh.
                    </p>
                    <button
                      onClick={async () => { await avail.leaveQueue(); toast.success("Left the queue"); }}
                      className="mt-3 rounded-full w-full py-2.5 text-sm font-medium border-2 border-amber-700 text-amber-900 hover:bg-amber-100 transition"
                      data-testid="leave-queue-btn"
                    >
                      Leave queue
                    </button>
                  </div>
                ) : avail.myStatus === "ready" ? (
                  // ─── It's our turn ────────────────────────────────────
                  <div
                    className="mt-5 rounded-2xl p-5 border-2 border-emerald-500 bg-emerald-50 text-emerald-900"
                    data-testid="queue-ready-card"
                  >
                    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest">
                      <Sparkle size={14} weight="fill" /> Your turn!
                    </div>
                    <div className="font-serif-display text-2xl mt-1 font-light">
                      {a.name.split(" ")[0]} is ready for you
                    </div>
                    <p className="text-xs mt-1.5 leading-relaxed">
                      Tap to start your {avail.myMode === "call" ? "call" : "chat"} now.
                    </p>
                    <button
                      onClick={async () => {
                        await avail.markStarted();
                        if (avail.myMode === "call") setShowPreCall(true);
                        else await startChat();
                      }}
                      className="mt-3 rounded-full w-full py-3 font-bold flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white shadow-md transition"
                      data-testid="start-now-btn"
                    >
                      {avail.myMode === "call" ? <Phone size={18} weight="fill" /> : <ChatCircleDots size={18} weight="fill" />}
                      Start {avail.myMode === "call" ? "call" : "chat"} now · {formatPrice(a.rate_per_min)}/min
                    </button>
                  </div>
                ) : (
                  // ─── Busy, not in queue → offer to join ────────────────
                  <div
                    className="mt-5 rounded-2xl p-5 border-2 border-amber-400 bg-amber-50 text-amber-900"
                    data-testid="busy-card"
                  >
                    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest">
                      <Clock size={14} weight="fill" /> Currently busy
                    </div>
                    <div className="font-serif-display text-2xl mt-1 font-light">
                      {a.name.split(" ")[0]} is in a{" "}
                      {avail.activeSession?.mode === "call" ? "call" : "chat"} right now
                    </div>
                    <div className="text-sm mt-2 flex items-center gap-1.5">
                      <Hourglass size={14} weight="fill" />
                      Available in approx{" "}
                      <span className="font-semibold tabular-nums">
                        {formatEta(avail.activeSession?.eta_seconds)}
                      </span>
                    </div>
                    <p className="text-[11px] mt-1.5 text-amber-800">
                      {avail.queueSize}/{avail.maxQueueSize} seekers ahead of you.
                    </p>
                    {avail.queueFull ? (
                      <div className="mt-3 rounded-full w-full py-3 px-4 text-center text-sm bg-amber-100 border border-amber-300 text-amber-900" data-testid="queue-full-msg">
                        Queue is full — please try another astrologer or come back in a few minutes.
                      </div>
                    ) : (
                      <div className="mt-3 grid grid-cols-2 gap-2">
                        {a.chat_enabled && (
                          <button
                            onClick={async () => {
                              if (!user) {
                                toast.info("Sign in to join the queue");
                                const redirectUrl = window.location.href;
                                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                                        startGoogleSignIn(redirectUrl);
                                return;
                              }
                              try {
                                await avail.joinQueue("chat");
                                toast.success("You're in the queue for chat");
                              } catch (e) {
                                toast.error(e?.response?.data?.detail || "Couldn't join queue");
                              }
                            }}
                            className="rounded-full py-2.5 text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm transition flex items-center justify-center gap-1.5"
                            data-testid="join-queue-chat-btn"
                          >
                            <ChatCircleDots size={14} weight="fill" /> Queue · Chat
                          </button>
                        )}
                        {a.call_enabled && (
                          <button
                            onClick={async () => {
                              if (!user) {
                                toast.info("Sign in to join the queue");
                                const redirectUrl = window.location.href;
                                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                                        startGoogleSignIn(redirectUrl);
                                return;
                              }
                              try {
                                await avail.joinQueue("call");
                                toast.success("You're in the queue for call");
                              } catch (e) {
                                toast.error(e?.response?.data?.detail || "Couldn't join queue");
                              }
                            }}
                            className="rounded-full py-2.5 text-sm font-semibold bg-amber-600 hover:bg-amber-700 text-white shadow-sm transition flex items-center justify-center gap-1.5"
                            data-testid="join-queue-call-btn"
                          >
                            <Phone size={14} weight="fill" /> Queue · Call
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {!a.is_ai && !a.chat_enabled && !a.call_enabled && (
                  <div className="rounded-full w-full mt-5 py-3 px-4 text-center text-sm bg-amber-50 border border-amber-200 text-amber-900" data-testid="astrologer-unavailable">
                    {a.name.split(" ")[0]} is currently offline. Try another astrologer or check back soon.
                  </div>
                )}
                {a.is_ai && !user?.free_trial_used && (
                  <p className="text-muted text-xs mt-3 text-center font-light">Your first 3 minutes are on us — no card required.</p>
                )}
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <p className="label-eyebrow">{a.specialization} astrologer{a.is_ai ? " · AI Expert" : ""}</p>
            <div className="flex items-start justify-between gap-4 mt-3">
              <h1 className="font-serif-display text-5xl sm:text-6xl font-light">{a.name}</h1>
              <button
                onClick={async () => {
                  hapticLight();
                  const url = `https://astrokuberchat.com/astrologers/${a.astrologer_id}`;
                  const r = await openShareSheet({
                    title: `${a.name} on AstroKuberChat`,
                    text: `${a.name} — ${a.specialization} astrologer${a.experience_years ? `, ${a.experience_years} years experience` : ""}. Chat on AstroKuberChat.`,
                    url,
                    dialogTitle: `Share ${a.name}`,
                  });
                  if (r.copied) toast.success("Link copied to clipboard");
                  else if (!r.ok && r.error) toast.error(r.error);
                }}
                className="mt-2 flex-shrink-0 inline-flex items-center gap-1.5 rounded-full border border-soft px-3 py-2 text-xs text-muted hover:text-gold hover:border-gold/50 transition-colors"
                data-testid="astrologer-share-btn"
                aria-label={`Share ${a.name}`}
              >
                <ShareNetwork size={14} weight="duotone" />
                Share
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-4 mt-5 text-sm">
              <span className="flex items-center gap-1.5" data-testid="detail-reviews"><Star size={16} weight="fill" className="text-gold" />{displayedRating} <span className="text-muted">({displayedReviewsCount.toLocaleString("en-IN")} reviews)</span></span>
              <span className="text-muted">·</span>
              <span>{a.experience_years} years experience</span>
              <span className="text-muted">·</span>
              <span className="text-muted">{a.languages.join(", ")}</span>
            </div>
            <p className="text-muted text-lg leading-relaxed mt-8 font-light max-w-xl" data-testid="astrologer-bio">{a.bio}</p>

            {a.technique && (
              <div className="mt-8 max-w-xl card-surface rounded-md p-5 border-l-4 border-[#D4AF37]" data-testid="astrologer-technique">
                <p className="label-eyebrow text-gold">Signature method</p>
                <p className="font-serif-display text-2xl mt-2 font-light">{a.technique}</p>
                <p className="text-muted text-sm mt-2 font-light">
                  This is the technique she&apos;ll use through your reading.
                </p>
              </div>
            )}

            <div className="mt-14">
              <p className="label-eyebrow">Recent reviews</p>
              <h3 className="font-serif-display text-3xl mt-3 font-light">What clients say</h3>
              <div className="mt-6 space-y-4">
                {(() => {
                  // Always show 5 *written* glowing reviews. Real reviews
                  // count toward the 5 ONLY when they include an actual
                  // comment — otherwise we top-up with curated 5-star
                  // testimonials so the section never looks empty. Real
                  // written reviews come first; curated entries fill the
                  // remaining slots in deterministic-per-astrologer order.
                  const realWritten = (data.reviews || [])
                    // Defensive UI filter — only 4★ and 5★ ever surface on
                    // the public astrologer page. The backend filters too;
                    // this is belt + suspenders.
                    .filter((r) => (r.rating || 5) >= 4)
                    .filter((r) => (r.comment || "").trim().length > 0)
                    .slice(0, 5);
                  const seeded = curatedFiveStarReviews(a.astrologer_id, data.curated_review_index);
                  const topUp = seeded.slice(0, Math.max(0, 5 - realWritten.length));
                  const merged = [...realWritten, ...topUp];
                  const maskName = (name) => {
                    if (!name) return "Anonymous";
                    const trimmed = String(name).trim();
                    const head = trimmed.slice(0, 3);
                    return head + (trimmed.length > 3 ? " " + "*".repeat(Math.min(8, trimmed.length - 3)) : "");
                  };
                  return merged.map((r) => (
                    <div key={r.review_id} className="card-surface rounded-md p-5" data-testid={`review-${r.review_id}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-medium" data-testid={`review-name-${r.review_id}`}>{maskName(r.customer_name)}</div>
                          {r.days_ago && (
                            <div className="text-[11px] text-muted mt-0.5">{r.days_ago}</div>
                          )}
                        </div>
                        <div className="flex items-center gap-1 text-gold shrink-0">{Array.from({ length: r.rating }).map((_, i) => <Star key={`${r.review_id}-${i}`} size={14} weight="fill" />)}</div>
                      </div>
                      {r.comment && <div className="text-muted text-sm mt-2 font-light leading-relaxed">{r.comment}</div>}
                    </div>
                  ));
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Voice-call modals ─── */}
      {showPreCall && (
        <PreCallModal
          astrologer={a}
          country={country}
          onClose={() => setShowPreCall(false)}
          onPickWebRTC={async () => {
            try {
              await voice.connect(`astrologer-${a.astrologer_id}`, a.astrologer_id);
              setShowPreCall(false);
            } catch (e) {
              // The 402 "insufficient balance" error thrown from
              // `voice.connect` has a full explanatory message on
              // `e.message` — surface it verbatim instead of the
              // generic mic-permission fallback.
              const raw = e?.message || "";
              const isInsufficient = /balance|wallet/i.test(raw);
              const msg = isInsufficient
                ? raw
                : (raw.toLowerCase().includes("permission")
                    ? "Please allow microphone access in your browser, then try again."
                    : "Couldn't start the in-app call. Try Phone call instead.");
              toast.error(msg);
            }
          }}
          onPickPSTN={async (phone) => {
            try {
              const { data: r } = await api.post("/voice/initiate-pstn", {
                astrologer_id: a.astrologer_id,
                seeker_phone: phone,
              });
              setPstnCallId(r.call_id);
              setShowPreCall(false);
              toast.success(r.message || "Calling your phone…");
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Couldn't start the phone call.");
            }
          }}
        />
      )}
      {voice.active && voice.active.direction === "out" && (
        <ActiveCallPanel
          astrologer={a}
          muted={voice.muted}
          onMute={voice.toggleMute}
          onHangup={voice.hangup}
        />
      )}
      {pstnCallId && (
        <PSTNRingingModal
          astrologer={a}
          callId={pstnCallId}
          onClose={() => setPstnCallId(null)}
        />
      )}

      {/* "Your turn!" popup — fires the instant the user is promoted from
          waiting → ready (the 5s availability poll picks this up). Floats
          above everything else; one tap starts the session. We render this
          even if the user has scrolled away from the side-card so they
          can't miss it. */}
      {avail.myStatus === "ready" && (
        <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="your-turn-modal">
          <div className="rounded-2xl shadow-2xl max-w-sm w-full p-6 sm:p-7" style={{ backgroundColor: "#FFFBF5", color: "#1A1410" }}>
            <div className="flex items-center justify-between">
              <p className="text-[10px] uppercase tracking-widest font-bold flex items-center gap-1.5" style={{ color: "#15803D" }}>
                <Sparkle size={12} weight="fill" /> Your turn!
              </p>
              <button
                onClick={async () => { await avail.leaveQueue(); toast.message("Left the queue"); }}
                aria-label="Dismiss"
                className="rounded-full p-1 hover:bg-amber-100"
                data-testid="your-turn-dismiss-btn"
              >
                <X size={16} weight="bold" style={{ color: "#92400E" }} />
              </button>
            </div>
            <h3 className="font-serif-display text-2xl mt-1.5 font-light">
              {a.name} is ready for you
            </h3>
            <p className="text-sm mt-2" style={{ color: "#6B7280" }}>
              Tap below to start your {avail.myMode === "call" ? "call" : "chat"}. You have 2 minutes to claim your slot — after that, the next person in queue takes over.
            </p>
            <button
              onClick={async () => {
                await avail.markStarted();
                if (avail.myMode === "call") setShowPreCall(true);
                else await startChat();
              }}
              className="mt-4 rounded-full w-full py-3.5 font-bold flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white shadow-md transition"
              data-testid="your-turn-start-btn"
            >
              {avail.myMode === "call" ? <Phone size={18} weight="fill" /> : <ChatCircleDots size={18} weight="fill" />}
              Start {avail.myMode === "call" ? "call" : "chat"} now · {formatPrice(a.rate_per_min)}/min
            </button>
          </div>
        </div>
      )}
    </PageShell>
  );
}
